import getDecryptedToken from "../../../../api/getDecryptedToken";

export function isRoomAllocationEnabled(): boolean {
  return getDecryptedToken("featureRoomAllocation") === "true";
}

export function getLocationColumnLabel(tokenOnly = false): string {
  if (tokenOnly) return "Token";
  return isRoomAllocationEnabled()
    ? "Session / Room / Token"
    : "Session / Room";
}

export const APPOINTMENT_TABLE_HEADERS = [
  { key: "datetime", label: "Date & Time", align: "left" as const },
  { key: "token", label: "Token", align: "center" as const },
  { key: "patient", label: "Patient", align: "left" as const },
  { key: "purpose", label: "Purpose", align: "left" as const },
  { key: "doctor", label: "Doctor", align: "left" as const },
  { key: "location", label: "Session / Room / Token", align: "left" as const },
  { key: "status", label: "Status", align: "left" as const },
  { key: "action", label: "Action", align: "center" as const },
];

/** Column order for dentist My Appointments table */
export const MY_APPOINTMENT_TABLE_HEADERS = [
  { key: "datetime", label: "Date & Time", align: "left" as const },
  { key: "token", label: "Token", align: "center" as const },
  { key: "patient", label: "Patient", align: "left" as const },
  { key: "purpose", label: "Purpose", align: "left" as const },
  { key: "patientType", label: "Status", align: "left" as const },
  { key: "status", label: "Action", align: "left" as const },
  { key: "action", label: "Profile", align: "center" as const },
];

export function patientTypeLabelFromItem(item: {
  patientTypeLabel?: string | null;
  registerdDate?: string | Date | null;
  createdAt?: string | Date | null;
  patientId?: {
    registerdDate?: string | Date | null;
    createdAt?: string | Date | null;
  };
}): string {
  if (item?.patientTypeLabel) return item.patientTypeLabel;

  const created =
    item?.registerdDate ||
    item?.createdAt ||
    item?.patientId?.registerdDate ||
    item?.patientId?.createdAt;
  if (created) {
    const d = created instanceof Date ? created : new Date(created);
    if (!Number.isNaN(d.getTime())) {
      const today = new Date();
      const sameDay =
        d.getFullYear() === today.getFullYear() &&
        d.getMonth() === today.getMonth() &&
        d.getDate() === today.getDate();
      if (sameDay) return "New Patient";
    }
  }
  return "Existing Patient";
}

/** Long-term estimate remaining due (same formula as invoice / case treatment). */
export function longTermDueFromTreatments(treatments: any[]): number {
  let due = 0;
  for (const item of treatments || []) {
    if (item?.status !== "long term") continue;
    const estimate = parseFloat(item?.price) || 0;
    const billed = (item?.subTreatment || []).reduce((sum: number, sub: any) => {
      const isPriced =
        sub?.selected_option === "treatment_paid" ||
        sub?.selected_option === "treatment_stage";
      if (
        !isPriced ||
        sub?.status === "long term" ||
        sub?.status === "planned"
      ) {
        return sum;
      }
      const price = parseFloat(sub?.price);
      return sum + (Number.isFinite(price) ? price : 0);
    }, 0);
    due += Math.max(0, estimate - billed);
  }
  return due;
}

export function resolvePatientTypeLabelFromPatientAndTreatments(
  patient: any,
  treatmentDocs: any[] = []
): string {
  if (patient?.patientTypeLabel === "Treatment Plan") return "Treatment Plan";

  const allItems = (treatmentDocs || []).flatMap(
    (doc) => doc?.treatments || doc?.treatment || []
  );
  if (longTermDueFromTreatments(allItems) > 0) return "Treatment Plan";

  return patientTypeLabelFromItem(patient || {});
}

export const PATIENT_TYPE_STYLES: Record<string, string> = {
  "Treatment Plan":
    "bg-amber-200 dark:bg-amber-500/30 text-amber-900 dark:text-amber-100 border-amber-400 dark:border-amber-500",
  "New Patient":
    "bg-emerald-200 dark:bg-emerald-500/30 text-emerald-900 dark:text-emerald-100 border-emerald-400 dark:border-emerald-500",
  "Existing Patient":
    "bg-indigo-200 dark:bg-indigo-500/30 text-indigo-900 dark:text-indigo-100 border-indigo-400 dark:border-indigo-500",
};

/** Short UI labels for patient visit type badges */
export const PATIENT_TYPE_SHORT_LABELS: Record<string, string> = {
  "Treatment Plan": "Plan",
  "New Patient": "New",
  "Existing Patient": "Exist",
};

export function formatPatientTypeLabel(label?: string | null): string {
  if (!label) return PATIENT_TYPE_SHORT_LABELS["Existing Patient"];
  return PATIENT_TYPE_SHORT_LABELS[label] || label;
}

export function patientMobileFromItem(item: {
  patientId?: { mobileNo?: string | null };
  bookPatientId?: { mobileNo?: string | null };
  mobileNo?: string | null;
}): string {
  return (
    item?.patientId?.mobileNo ||
    item?.bookPatientId?.mobileNo ||
    item?.mobileNo ||
    "—"
  );
}

export const APPOINTMENT_STATUS_LABELS: Record<string, string> = {
  appointment: "Upcoming",
  walkin: "Walk-in",
  callin: "Call-in",
  waiting: "Waiting",
  inqueue: "In Queue",
  ongoing: "Ongoing",
  completed: "Completed",
  canceled: "Canceled",
  cancelled: "Canceled",
  noshow: "No Show",
  tray: "Reschedule Tray",
};

export const APPOINTMENT_STATUS_OPTIONS = [
  { value: "appointment", label: "Upcoming" },
  { value: "walkin", label: "Walk-in" },
  { value: "callin", label: "Call-in" },
  { value: "waiting", label: "Waiting" },
  { value: "inqueue", label: "In Queue" },
  { value: "ongoing", label: "Ongoing" },
  { value: "completed", label: "Completed" },
  { value: "canceled", label: "Canceled" },
  { value: "noshow", label: "No Show" },
] as const;

export const statusStyles: Record<string, string> = {
  ongoing:
    "bg-lime-100 dark:bg-lime-900/40 text-lime-700 dark:text-lime-300 border-lime-300 dark:border-lime-600",
  waiting:
    "bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-600",
  inqueue:
    "bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 border-sky-300 dark:border-sky-600",
  canceled:
    "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 border-red-300 dark:border-red-600",
  completed:
    "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-600",
  appointment:
    "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-600",
  walkin:
    "bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 border-teal-300 dark:border-teal-600",
  callin:
    "bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 border-sky-300 dark:border-sky-600",
  reschudle:
    "bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 border-cyan-300 dark:border-cyan-600",
  noshow:
    "bg-slate-100 dark:bg-slate-600 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-500",
  default:
    "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600",
};

export const FIELD_LABELS = {
  dateTime: "Date & Time",
  location: "Session / Room / Token",
  doctor: "Doctor",
  status: "Status",
  purpose: "Purpose",
  type: "Type",
  queue: "Token number",
} as const;

export function formatAppointmentStatus(status?: string | null): string {
  const key = String(status ?? "").trim();
  if (!key) return "—";
  return APPOINTMENT_STATUS_LABELS[key] ?? key;
}

export function getLocationFieldLabel(tokenOnly = false): string {
  return getLocationColumnLabel(tokenOnly);
}

/** Parse queue input; empty string → 0. Returns null if invalid. */
export function parseQueueInput(raw: string): number | null {
  const trimmed = String(raw ?? "").trim();
  if (trimmed === "") return 0;
  if (!/^\d+$/.test(trimmed)) return null;
  const n = parseInt(trimmed, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function formatQueueFromItem(item: { queue?: unknown }): string {
  if (item?.queue != null && item?.queue !== "") {
    return String(Number(item.queue));
  }
  return "";
}

function employeeDisplayName(
  employee?: { firstName?: string; lastName?: string } | null
): string {
  if (!employee) return "";
  return [employee.firstName, employee.lastName].filter(Boolean).join(" ").trim();
}

export function formatAppointmentPurpose(item?: {
  purposeName?: string | string[];
  purposeIds?: Array<{ purposeName?: string } | string>;
  purposeId?: { purposeName?: string | string[] };
  bookTreatmentId?: { name?: string };
} | null): string {
  const fromIds = (item?.purposeIds ?? [])
    .map((p) => (typeof p === "object" && p != null ? p.purposeName : null))
    .filter((name): name is string => Boolean(name && String(name).trim()));
  if (fromIds.length) return fromIds.join(", ");

  const topLevel = item?.purposeName;
  if (Array.isArray(topLevel)) {
    const names = topLevel.map((n) => String(n).trim()).filter(Boolean);
    if (names.length) return names.join(", ");
  } else if (topLevel != null && String(topLevel).trim()) {
    return String(topLevel).trim();
  }

  const single = item?.purposeId?.purposeName;
  if (Array.isArray(single)) {
    const names = single.map((n) => String(n).trim()).filter(Boolean);
    if (names.length) return names.join(", ");
  } else if (single != null && String(single).trim()) {
    return String(single).trim();
  }
  if (item?.bookTreatmentId?.name) return String(item.bookTreatmentId.name);
  return "N/A";
}

/** First purpose color on the appointment (purposeIds, then purposeId). */
export function purposeColorFromItem(item?: {
  purposeColor?: string;
  purposeIds?: Array<{ color?: string } | string>;
  purposeId?: { color?: string };
} | null): string | null {
  const top = item?.purposeColor;
  if (top != null && String(top).trim()) return String(top).trim();
  const fromIds = (item?.purposeIds ?? [])
    .map((p) => (typeof p === "object" && p != null ? p.color : null))
    .find((c) => c != null && String(c).trim());
  if (fromIds) return String(fromIds).trim();
  const single = item?.purposeId?.color;
  if (single != null && String(single).trim()) return String(single).trim();
  return null;
}

/** Soft background tint from a purpose color (matches Manage Purpose swatches). */
export function purposeColorBackground(
  color: string,
  opacityPercent = 30
): string {
  return `color-mix(in srgb, ${color} ${opacityPercent}%, transparent)`;
}

export function purposeIdsFromAppointment(item?: {
  purposeIds?: Array<{ _id?: string; id?: string } | string>;
  purposeId?: { _id?: string; id?: string } | string;
} | null): string[] {
  const fromIds = (item?.purposeIds ?? [])
    .map((p) => (typeof p === "object" && p != null ? p._id ?? p.id : p))
    .filter((id) => id != null && String(id).trim())
    .map((id) => String(id));
  if (fromIds.length) return fromIds;
  const single = item?.purposeId;
  if (single == null) return [];
  if (typeof single === "object") {
    const id = single._id ?? single.id;
    return id != null ? [String(id)] : [];
  }
  return [String(single)];
}

export function formatPurposeIdsFromCatalog(
  purposeIds: string[],
  purposesCatalog?: Array<{ _id?: string; id?: string; purposeName?: string }>
): string {
  const names = purposeIds
    .map((id) => {
      const match = (purposesCatalog ?? []).find(
        (item) => String(item._id ?? item.id) === String(id)
      );
      return match?.purposeName ? String(match.purposeName) : null;
    })
    .filter((name): name is string => Boolean(name));
  if (names.length) return names.join(", ");
  return purposeIds.length ? "—" : "Not selected";
}

export function doctorNameFromItem(item: {
  doctorId?: { firstName?: string; lastName?: string };
}): string {
  return employeeDisplayName(item?.doctorId) || "—";
}

/** Names from tranferdoctor history (DB field name kept as-is). */
export function transferDoctorNamesFromItem(item: {
  tranferdoctor?: Array<{ doctorId?: { firstName?: string; lastName?: string } }>;
}): string {
  const list = item?.tranferdoctor;
  if (!Array.isArray(list) || list.length === 0) return "";
  return list
    .map((entry) => employeeDisplayName(entry?.doctorId))
    .filter(Boolean)
    .join(", ");
}

export function latestTransferDoctorNameFromItem(item: {
  tranferdoctor?: Array<{ doctorId?: { firstName?: string; lastName?: string } }>;
}): string {
  const list = item?.tranferdoctor;
  if (!Array.isArray(list) || list.length === 0) return "";
  return employeeDisplayName(list[list.length - 1]?.doctorId);
}
